import { Component, type ReactNode } from "react";
import {
  canAutoRecover,
  forceRecoveryReload,
  isChunkLoadError,
  tryRecover,
} from "@/shared/lib/chunkReloadRecovery";
import { cautionIcon } from "@/shared/ui/alertDialogIcons";
import PrimaryButton from "@/shared/ui/PrimaryButton";
import styles from "./RouteErrorBoundary.module.scss";

/** null 렌더(복구 대기) 상태에서 replace 내비게이션이 이 시간 안에 완료되지 않으면
 *  (오프라인 등으로 문서가 살아남은 경우) 안내 화면으로 전환 — 영구 빈 화면 방지. */
const NAV_STALL_MS = 8_000;

type RouteErrorBoundaryProps = {
  children: ReactNode;
};

type RouteErrorBoundaryState = {
  hasError: boolean;
  error: unknown;
  /** 복구 리로드 예약 후 내비게이션 정체 판정 — true 면 null 대신 안내 화면을 렌더. */
  stalled: boolean;
};

/**
 * 청크 로드 실패(배포 세대 불일치) 전용 에러 경계 (#198).
 *
 * 왜 boundary 인가: 이 앱에는 ErrorBoundary 가 없어 lazy 렌더 에러가 잡히지 않으면 React 가
 * 루트 전체를 언마운트한다 — AlertDialogHost 도 함께 죽으므로 "가드 발동 시 팝업 안내"는
 * 구조적으로 불성립. 라우트 subtree 만 감싸는 이 경계가 잡으면 형제 호스트들은 생존한다.
 *
 * 렌더 3분기:
 * - 비-청크 에러 → rethrow. 현행 동작(전파→언마운트)을 그대로 보존한다 — 이 경계는 청크 복구
 *   전용이며 일반 렌더 에러 UX 를 바꾸지 않는다.
 * - 청크 에러 + 복구 진행·예정(canAutoRecover) → null. 현행 Suspense fallback 과 동일한 빈
 *   화면을 유지한 채 replace 내비게이션 완료를 기다린다 (안내 화면 플래시 방지).
 * - 청크 에러 + 가드(리로드 직후 재실패) 또는 내비 정체 → 전체화면 안내 + [다시 시도하기].
 */
class RouteErrorBoundary extends Component<RouteErrorBoundaryProps, RouteErrorBoundaryState> {
  state: RouteErrorBoundaryState = { hasError: false, error: null, stalled: false };

  private stallTimer: number | undefined;

  static getDerivedStateFromError(error: unknown): Partial<RouteErrorBoundaryState> {
    return { hasError: true, error };
  }

  componentDidCatch(error: unknown) {
    if (!isChunkLoadError(error)) return;
    // 감지 지점 ④ — bare import() 실패는 React.lazy 가 rejection 을 소비해 window 이벤트가
    // 발화하지 않으므로 여기서 funnel 로 수렴시킨다. 리스너가 이미 예약했으면 idempotent(true).
    tryRecover();
    if (canAutoRecover()) {
      this.stallTimer = window.setTimeout(() => this.setState({ stalled: true }), NAV_STALL_MS);
    }
  }

  componentWillUnmount() {
    window.clearTimeout(this.stallTimer);
  }

  // 수동 트리거라 루프 위험 없음(누를 때마다 1회) — 가드를 우회하는 직접 리로드 경로.
  handleRetry = () => {
    forceRecoveryReload();
  };

  render() {
    const { hasError, error, stalled } = this.state;
    if (!hasError) return this.props.children;
    if (!isChunkLoadError(error)) throw error;
    if (canAutoRecover() && !stalled) return null;

    return (
      <div className={styles.fallback} role="alert">
        <img src={cautionIcon} alt="" className={styles.icon} />
        <p className={styles.title}>새 버전을 불러오지 못했어요</p>
        <p className={styles.message}>{"네트워크 상태를 확인한 뒤\n다시 시도해 주세요"}</p>
        <PrimaryButton onClick={this.handleRetry} className={styles.retryButton}>
          다시 시도하기
        </PrimaryButton>
      </div>
    );
  }
}

export default RouteErrorBoundary;
